/**
 * The single country/nationality **name → registry entry** resolver.
 *
 * The backend's `Nationality` type carries only `id` and `name` — there is no
 * ISO code on the wire — so every flag in the app is resolved from a free-text
 * display name against the registry in `@/config/constants`. Before SE-6241
 * three call sites did that lookup independently and drifted apart:
 *
 * - the player-profile header normalised four feed spellings ("Türkiye",
 *   "Korea, South", "Cote d'Ivoire", "Czech Republic" — SE-4208) but matched
 *   the registry case-sensitively and knew none of the feeds' abbreviations,
 *   so "USA" and "UAE" resolved to nothing;
 * - the Similar Players list matched case-insensitively and knew the
 *   abbreviations, but had no spelling normalisation, so the four names
 *   SE-4208 fixed on the profile were still blank in the list — which is the
 *   defect SE-6241 reports;
 * - `HeaderPlayer` did a bare case-sensitive `find` with neither.
 *
 * Each path therefore had coverage the others lacked, and adding the reported
 * countries to whichever table was short would only have re-set the trap. This
 * module is the one lookup all of them now share.
 *
 * Resolution order for a name, first hit wins:
 *
 * 1. Normalised exact match against the registry. Normalisation folds case,
 *    combining diacritics, curly apostrophes and punctuation, so
 *    "Cote d'Ivoire" and "Côte d’Ivoire" are the same key.
 * 2. Comma inversion — "Korea, South" is also tried as "South Korea". The feed
 *    emits inverted names; no registry name contains a comma, so this can
 *    never shadow a real entry.
 * 3. {@link NAME_ALIASES} — names normalisation cannot bridge, because the
 *    country was renamed ("Türkiye" → "Turkey", "Czech Republic" → "Czechia")
 *    or the feed abbreviates it ("USA", "UAE", "UK").
 *
 * Flag *presentation* choices stay with the caller: this module answers "which
 * country is this?", not "which glyph should it wear". That is why the
 * England/Scotland/Wales → Union Jack decision lives in `countryFlag.ts` and
 * not here.
 * @module utils/nationalityRegistry
 */
import { nationalities } from "@/config/constants";

/** One row of the country registry in `@/config/constants`. */
export type NationalityRegistryEntry = (typeof nationalities)[number];

/** Platform identifiers that select a flag asset format. */
export type FlagPlatform = "ios" | "android" | "web" | "windows" | "macos";

/** PNG flag CDN base URL for native platforms (RN `Image` cannot render SVG). */
const FLAG_PNG_CDN = "https://flagcdn.com/48x36/";

/**
 * Folds a display name to a comparison key.
 *
 * Strips combining diacritics, unifies the three apostrophe code points the
 * feeds mix, lowercases, and collapses every remaining non-alphanumeric run to
 * a single space.
 * @param raw - Country or nationality name as displayed
 * @returns The comparison key, e.g. "cote d ivoire"
 */
const normalizeName = (raw: string): string =>
  raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/[\u2018\u2019\u02bc]/gu, "'")
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, " ")
    .trim();

/**
 * Names no amount of normalisation can bridge to a registry entry, keyed by
 * normalised name → normalised registry name.
 *
 * Two kinds only, and both are evidenced by a shipped defect rather than
 * guessed at: countries the registry lists under a superseded or successor
 * name (SE-4208), and the abbreviations the feeds send in place of the full
 * name (SE-4847's picker map). Everything else belongs in the registry.
 */
const NAME_ALIASES: Readonly<Record<string, string>> = {
  // Renames — the registry still uses the older name (SE-4208).
  turkiye: "turkey",
  "czech republic": "czechia",
  // Abbreviations and short forms the feeds emit (SE-4847).
  usa: "united states",
  uk: "united kingdom",
  "great britain": "united kingdom",
  uae: "united arab emirates",
  korea: "south korea",
};

/**
 * Registry rows keyed by normalised name.
 * @remarks Built once at module load; the registry is a frozen constant.
 */
const REGISTRY_BY_NAME: ReadonlyMap<string, NationalityRegistryEntry> = new Map(
  nationalities.map(entry => [normalizeName(entry.name), entry])
);

/**
 * Every key worth trying for a raw name, in resolution order.
 * @param raw - The caller's country or nationality name
 * @returns Candidate comparison keys, most literal first
 */
const candidateKeys = (raw: string): readonly string[] => {
  const direct = normalizeName(raw);
  const commaIndex = raw.indexOf(",");
  const inverted =
    commaIndex > 0
      ? normalizeName(
          `${raw.slice(commaIndex + 1)} ${raw.slice(0, commaIndex)}`
        )
      : null;

  return [
    direct,
    ...(inverted ? [inverted] : []),
    ...(NAME_ALIASES[direct] ? [NAME_ALIASES[direct]] : []),
  ];
};

/**
 * Resolves a country or nationality display name to its registry entry.
 * @param name - Name as returned by `Nationality.name`, `League.country` or
 *   `bioData.leagueCountry` (e.g. "Spain", "Korea, South", "USA")
 * @returns The registry entry, or null when the name matches no country
 */
export const resolveNationality = (
  name: string | null | undefined
): NationalityRegistryEntry | null => {
  if (!name) return null;

  for (const key of candidateKeys(name)) {
    const entry = REGISTRY_BY_NAME.get(key);
    if (entry) return entry;
  }

  return null;
};

/**
 * Builds the flag asset URL for a registry entry on the given platform.
 * @param entry - A registry entry, typically from {@link resolveNationality}
 * @param platform - Platform selecting the asset format
 * @returns SVG URL on web, PNG URL on native
 * @remarks Web takes the SVG the registry already carries; native takes a PNG
 *   from flagcdn because React Native's `Image` cannot render SVG.
 */
export const flagUrlForNationality = (
  entry: NationalityRegistryEntry,
  platform: FlagPlatform
): string =>
  platform === "web"
    ? entry.image
    : `${FLAG_PNG_CDN}${entry.code.toLowerCase()}.png`;
